import {
    AnalyticsButtonInfo,
    TicketData,
    TicketTimeData,
} from '@beat-venues/common-types';
import { urlUtil } from '@beat-venues/utils';
import { useTranslation } from 'next-i18next';
import { type ReactNode, useState, type JSX } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import { Button, Heading, StatusLabel, Text } from '../../../atoms';
import type { CommonProps } from '../../../types';
import { useAnalytics } from '../../../useAnalytics';
import { ReminderButton } from '../../reminder-button';
import { EventSeriesSensoryIcons } from '../../event-series-sensory-icons';

export interface VenueTicketsEventTimeProps
    extends CommonProps,
        VariantProps<typeof VenueTicketsEventTimeStyles> {
    children?: ReactNode;
    ticket: TicketData;
    time: TicketTimeData;
    onBuyTicketClick?: (value: AnalyticsButtonInfo) => void;
    eventTitle: string;
}

export const VenueTicketsEventTimeStyles = tv({
    slots: {
        root: 'flex flex-col gap-y-2',
    },
    variants: {},
});

export const VenueTicketsEventTime = ({
    className,
    testId = 'venue-tickets-event-times',
    time,
    ticket,
    eventTitle,
    onBuyTicketClick,
}: VenueTicketsEventTimeProps): JSX.Element => {
    const { root } = VenueTicketsEventTimeStyles();
    const { t } = useTranslation(['global', 'event']);
    const [modalIsShown, setModalIsShown] = useState(false);
    const camefromParam = useAnalytics();
    return (
        <li key={time.id} className={root({ className })} data-testid={testId}>
            {time.name && (
                <Heading as="h4" className="mb-2 font-light md:mb-4" size="h5">
                    {time.name}
                </Heading>
            )}
            <div className="flex flex-wrap items-center justify-between gap-y-4">
                {time.status && (
                    <div className="block w-full shrink-0 md:order-3 md:w-auto">
                        <StatusLabel className="mb-2">
                            {time.status}
                        </StatusLabel>
                    </div>
                )}
                <div className="flex gap-2 md:gap-3">
                    {time.time && (
                        <Text
                            className="xs:leading-8 mt-1 w-16 self-start"
                            size="2xl"
                        >
                            {time.time}
                        </Text>
                    )}
                    <div className="xs:flex grid gap-2 md:gap-3">
                        <div className="flex">
                            <div className="relative">
                                {time.cta && (
                                    <Button
                                        className="relative z-30 whitespace-nowrap px-3 sm:px-6"
                                        disabled={time.cta.isDisabled}
                                        href={
                                            time.cta.url &&
                                            urlUtil.buildBuyButtonUrl(
                                                time.cta.url,
                                                camefromParam
                                            )
                                        }
                                        id={`buy-tickets-event-${time.id}`}
                                        onClick={() => {
                                            const buttonInfo: AnalyticsButtonInfo =
                                                {
                                                    event: 'button_click',
                                                    id: `buy-tickets-event-${time.id}`,
                                                    name: 'buy tickets',
                                                    text: t(
                                                        `ctaStatusLabels.${time?.cta?.label}`,
                                                        {
                                                            ns: 'event',
                                                        }
                                                    ),
                                                    category:
                                                        'event ticket link',
                                                    type: 'cta',
                                                    target_url:
                                                        time.cta?.url || '',
                                                };
                                            if (onBuyTicketClick) {
                                                onBuyTicketClick(buttonInfo);
                                            }
                                        }}
                                    >
                                        {/\d/.test(time.cta.label)
                                            ? time.cta.label
                                            : t(
                                                  `ctaStatusLabels.${time.cta.label}`,
                                                  {
                                                      ns: 'event',
                                                  }
                                              )}
                                    </Button>
                                )}
                                {time.reminder && (
                                    <div className="absolute left-1/2 top-1/2 z-20 box-content h-full w-full -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-gray-900 bg-gray-900"></div>
                                )}
                            </div>

                            {time.reminder && (
                                <ReminderButton
                                    eventData={{
                                        title: eventTitle,
                                        label: t(`reminder.cta.setReminder`, {
                                            ns: 'event',
                                        }),
                                        icalLabel: t(`reminder.label.ical`, {
                                            ns: 'event',
                                        }),
                                    }}
                                    location={
                                        t(`venues.${ticket.venueId}.name`, {
                                            ns: 'global',
                                        }) ?? undefined
                                    }
                                    modalIsShown={modalIsShown}
                                    reminderDate={time.reminder.originalDate}
                                    setModalIsShown={setModalIsShown}
                                    variant="small"
                                    id={`set-reminder-event-${time.id}`}
                                    onClick={() => {
                                        const buttonInfo: AnalyticsButtonInfo =
                                            {
                                                event: 'button_click',
                                                id: `set-reminder-event-${time.id}`,
                                                name: 'set reminder',
                                                text: t(
                                                    `reminder.cta.setReminder`,
                                                    {
                                                        ns: 'event',
                                                    }
                                                ),
                                                category: 'event reminder cta',
                                                type: 'cta',
                                                target_url: '',
                                            };
                                        if (onBuyTicketClick) {
                                            onBuyTicketClick(buttonInfo);
                                        }
                                    }}
                                />
                            )}
                        </div>

                        {time.waitingListUrl && (
                            <Button
                                className="whitespace-nowrap px-3 sm:px-6"
                                href={time.waitingListUrl}
                                intent="secondary"
                                id={`waiting-list-event-${time.id}`}
                                onClick={() => {
                                    const buttonInfo: AnalyticsButtonInfo = {
                                        event: 'button_click',
                                        id: `waiting-list-event-${time.id}`,
                                        name: 'waiting list',
                                        text: t(
                                            'vipTickets.ctaLabels.waitingList',
                                            {
                                                ns: 'event',
                                            }
                                        ),
                                        category: 'waiting list link',
                                        type: 'cta',
                                        target_url: time.waitingListUrl || '',
                                    };
                                    if (onBuyTicketClick) {
                                        onBuyTicketClick(buttonInfo);
                                    }
                                }}
                            >
                                {t('vipTickets.ctaLabels.waitingList', {
                                    ns: 'event',
                                })}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            {time.tags && time.tags.length > 0 && (
                <EventSeriesSensoryIcons tags={time.tags} />
            )}
        </li>
    );
};
